import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, SESSION_EXPIRED_EVENT, tokenStore } from '@/lib/api';
import type { AuthResponse, Role, User } from '@/types/api';

interface AuthContextValue {
  user: User | null;
  initializing: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  /** Adopte une session ouverte hors du formulaire de connexion (lancement LTI). */
  adoptSession: (tokens: { accessToken: string; refreshToken: string }, profile: User) => void;
  refreshUser: () => Promise<void>;
  updateUser: (user: User) => void;
  hasRole: (...roles: Role[]) => boolean;
  isStaff: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STAFF_ROLES: Role[] = ['SUPER_ADMIN', 'ADMIN', 'INSTRUCTOR', 'AUDITOR'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!tokenStore.access) {
      setUser(null);
      return;
    }
    try {
      const { user: profile } = await api.get<{ user: User }>('/auth/me');
      setUser(profile);
    } catch {
      tokenStore.clear();
      setUser(null);
    }
  }, []);

  // Restauration de la session au chargement de l'application.
  useEffect(() => {
    void loadProfile().finally(() => setInitializing(false));
  }, [loadProfile]);

  // Le client HTTP signale les sessions expirees apres echec du rafraichissement.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await api.post<AuthResponse>('/auth/login', { email, password });
    tokenStore.set(response.accessToken, response.refreshToken);
    setUser(response.user);
    return response.user;
  }, []);

  const adoptSession = useCallback(
    (tokens: { accessToken: string; refreshToken: string }, profile: User) => {
      tokenStore.set(tokens.accessToken, tokens.refreshToken);
      setUser(profile);
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout', { refreshToken: tokenStore.refresh });
    } catch {
      /* la deconnexion locale prime sur l'echec reseau */
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      initializing,
      isAuthenticated: Boolean(user),
      signIn,
      signOut,
      adoptSession,
      refreshUser: loadProfile,
      updateUser: setUser,
      hasRole: (...roles: Role[]) => Boolean(user && roles.includes(user.role)),
      isStaff: Boolean(user && STAFF_ROLES.includes(user.role)),
    }),
    [user, initializing, signIn, signOut, adoptSession, loadProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
